import React, { useCallback, useEffect, useState } from "react";
import Accordion from "../../../../components/Accordion";
import PricingDefaultsLoader from "./loader";
import useCommonStore from "../../../../stores/common";
import useOrganizationStore from "../../../../stores/organization";
import styles from "./styles.module.scss";
import toast from "react-hot-toast";
import Switch from "../../../../components/form/switch";
import Button from "../../../../components/new-button";
import TextInput from "../../../../components/form/text-input";
import {
  getOrganizationPricingDefaults,
  getActivePricingModels,
  setActivePricingModels,
  setPricingModelDefaults,
  getSystemDefaults,
  setSharedDefaults,
} from "../../../../../api";

const pricingModels = [
  { key: "dual_pricing", title: "Dual Pricing" },
  { key: "kurv_pricing", title: "Kurv Pricing" },
  { key: "flat_rate", title: "Flat Rate" },
  { key: "tiered", title: "Tiered" },
  { key: "surcharge", title: "Surcharge" },
  { key: "ic_plus", title: "IC Plus" },
  { key: "ach", title: "ACH" },
  { key: "common_defaults", title: "Common" },
  // { key: "default_fees", title: "Default Fees" },
];

const PricingModelAccordionItem = ({
  model,
  title,
  isActive,
  onToggleActive,
  defaults,
  onSaveDefaults,
  loading,
  readOnly,
  isExpanded,
}) => {
  const [localDefaults, setLocalDefaults] = useState(defaults);
  const [modelDefaults, setModelDefaults] = useState({});
  const [defaultFees, setDefaultFees] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalDefaults(defaults);

    let modelDefaults = {};
    let defaultFees = {};
    Object.entries(defaults).forEach(([key, value]) => {
      if(key.includes(model)) {
        modelDefaults[key] = value;
      } else {
        defaultFees[key] = value;
      }
    });    

    setModelDefaults(modelDefaults);
    setDefaultFees(defaultFees);

  }, [defaults]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveDefaults(model, localDefaults);
      toast.success(`${title} defaults saved successfully`, { duration: 5000 });
    } catch (error) {
      toast.error(`${error?.data?.message}`, { duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, type, value, VisibilityControl = false) => {
    setLocalDefaults((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [type]: value,
      },
    }));

    if (VisibilityControl) {
      setDefaultFees((prev) => ({
      ...prev,
        [field]: {
          ...prev[field],
          [type]: value,
        },
      }));
    }else {
      setModelDefaults((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [type]: value,
        },
      }));
    }
  };

  const handleInputBlur = (field, type, value) => {
    const numValue = parseFloat(value);
    const currentDefaults = localDefaults[field] || {};
    if (isNaN(numValue)) {
      return;
    }

    // Validate soft_limit
    if (type === 'soft_limit') {
      const defaultValue = parseFloat(currentDefaults.default) || 0;
      const minimumValue = parseFloat(currentDefaults.minimum) || 0;
      if (numValue <= minimumValue) {
        toast.error(`Soft limit must be greater than minimum value (${minimumValue})`, { duration: 5000 });
        return;
      }
      if (numValue >= defaultValue) {
        toast.error(`Soft limit must be less than default value (${defaultValue})`, { duration: 5000 });
        return;
      }      
    }
    
    // Validate default against soft_limit
    if (type === 'default') {
      const softLimit = parseFloat(currentDefaults.soft_limit) || 0;
      if (softLimit > 0 && numValue > softLimit) {
        toast.error(`Default value cannot be greater than soft limit (${softLimit})`, { duration: 5000 });
        return;
      }
    }

    // Validate minimum against soft_limit
    if (type === 'minimum') {
      const softLimit = parseFloat(currentDefaults.soft_limit) || 0;
      if (softLimit > 0 && numValue > softLimit) {
        toast.error(`Minimum value cannot be greater than soft limit (${softLimit})`, { duration: 5000 });
        return;
      }
    }
  };

  const onToggleVisibility = (field, visible) => {
    setLocalDefaults((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        visible: visible,
      },
    }));

    setDefaultFees((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        visible: visible,
      },
    }));
  };

  const renderFeeItem = (field, config, disabled, VisibilityControl = false) => {
    if (!config) return null;

    return (
      <div key={field} className={styles.feeItem}>
        <div className={styles.feeItemHeader}>
          <h3 className={styles.feeItemLabel}>
            {field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </h3>

          {VisibilityControl && (
            <div className={styles.visibilityControl}>
              <Switch
                id={`${field}_visibility`}
                name={`${field}_visibility`}
                checked={config.visible}
                onChange={() => onToggleVisibility(field, !config.visible)}
              />
            </div>
          )}
        </div>

        <div className={styles.feeItemInputs}>
          <TextInput
            label="Default"
            placeholder="0.00"
            id={`${field}_default`}
            name={`${field}_default`}
            inputMode="decimal"
            value={config.default ?? ""}
            disabled={disabled || (VisibilityControl && !config.visible)}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralDecimalMark: ".",
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleInputChange(field, "default", e.target.rawValue, VisibilityControl);
              },
            }}
            onBlur={(e) => {
              handleInputBlur(field, "default", e.target.value);
            }}
          />

          <TextInput
            label="Minimum"
            placeholder="0.00"
            id={`${field}_minimum`}
            name={`${field}_minimum`}
            inputMode="decimal"
            value={config.minimum ?? ""}
            disabled={disabled || (VisibilityControl && !config.visible)}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralDecimalMark: ".",
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleInputChange(field, "minimum", e.target.rawValue, VisibilityControl);
              },
            }}
            onBlur={(e) => {
              handleInputBlur(field, "minimum", e.target.value);
            }}
          />

          <TextInput
            label="Soft Limit"
            placeholder="0.00"
            id={`${field}_soft_limit`}
            name={`${field}_soft_limit`}
            inputMode="decimal"
            value={config.soft_limit ?? ""}
            disabled={disabled || (VisibilityControl && !config.visible)}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralDecimalMark: ".",
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleInputChange(field, "soft_limit", e.target.rawValue, VisibilityControl);
              },
            }}
            onBlur={(e) => {
              handleInputBlur(field, "soft_limit", e.target.value);
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.pricingModelAccordion}>
      {!readOnly && (
        <div className={styles.accordionHeader}>
          <div className={styles.modelInfo}>
            <div className={styles.modelInfoText}>
              <h3>Visibility Control</h3>
              <h4>
                {`Toggle to ${isActive ? "hide" : "show"} ${title} option on merchant application form.`}
              </h4>
            </div>

            <div className={styles.switchContainer}>
              <Switch
                id={`${model}_toggle`}
                name={`${model}_toggle`}
                checked={isActive}
                onChange={() => onToggleActive(model, title, !isActive)}
                labelPosition="right"
              />
            </div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div className={styles.accordionContent}>
          <div className={styles.defaultsContainer}>
            {Object.entries(modelDefaults).map(([field, config]) =>
              renderFeeItem(field, config, !isActive),
            )}
          </div>

          {/* <div className={styles.saveButton}>
            <Button
              onClick={handleSave}
              disabled={loading || saving || !isActive}
              isLoading={saving}
            >
              Save {title} Defaults
            </Button>
          </div> */}
        </div>
      )}

      <div className={styles.accordionHeader}>
        <div className={styles.modelInfo}>
          <div className={styles.modelInfoText}>
            <h3>Default Fees</h3>
            <h4>
              {`Set default fees for ${title}.`}
            </h4>
          </div>
        </div>
      </div>


      {isExpanded && (
        <div className={styles.accordionContent}>
          <div className={styles.defaultsContainer}>
            {Object.entries(defaultFees).map(([field, config]) =>
              renderFeeItem(field, config, !isActive, true),
            )}
          </div>

          <div className={styles.saveButton}>
            <Button
              onClick={handleSave}
              disabled={loading || saving || !isActive}
              isLoading={saving}
            >
              Save {title} Defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const PricingDefaults = () => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const [expandedItems, setExpandedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingActive, setSavingActive] = useState(false);
  const [defaults, setDefaults] = useState({});
  const [activeModels, setActiveModels] = useState({});

  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.id,
  );

  const fetchData = useCallback(async () => {
    if (!selectedOrganizationId) return;

    setLoading(true);
    try {
      const [defaultsResponse, activeModelsResponse] = await Promise.all([
        getOrganizationPricingDefaults(selectedOrganizationId),
        getActivePricingModels(selectedOrganizationId),
      ]);

      if (defaultsResponse?.data) {
        setDefaults(defaultsResponse.data);
      }

      if (activeModelsResponse?.data) {
        setActiveModels(activeModelsResponse.data);
      }
    } catch (error) {
      console.error("Error fetching pricing defaults:", error);
      toast.error("Error loading pricing defaults", { duration: 5000 });
      // Set default active models if API fails
      setActiveModels({
        dual_pricing: 1,
        flat_rate: 1,
        tiered: 1,
        surcharge: 1,
        ic_plus: 1,
        ach: 1,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizationId]);

  const handleToggleActive = async (model, title, isActive) => {
    setSavingActive(true);
    try {
      const newActiveModels = {
        ...activeModels,
        [model]: isActive ? 1 : 0,
      };

      const response = await setActivePricingModels(
        selectedOrganizationId,
        newActiveModels,
      );

      if (response?.data) {
        setActiveModels(newActiveModels);
        toast.success(`${title} ${isActive ? "activated" : "deactivated"}`, { duration: 5000 });
      } else {
        throw new Error("Failed to update active models");
      }
    } catch (error) {
      console.error("Error updating active models:", error);
      toast.error("Error updating active models", { duration: 5000 });
    } finally {
      setSavingActive(false);
    }
  };

  const handleSaveDefaults = async (model, modelDefaults) => {
    try {
      let response;

      // Handle shared items separately
      if (model === "common_defaults" || model === "default_fees") {
        response = await setSharedDefaults(
          selectedOrganizationId,
          model,
          modelDefaults,
        );
      } else {
        response = await setPricingModelDefaults(
          selectedOrganizationId,
          model,
          modelDefaults,
        );
      }

      if (response?.data) {
        setDefaults((prev) => ({
          ...prev,
          [model]: modelDefaults,
        }));
      } else {
        throw new Error("Failed to save defaults");
      }
    } catch (error) {
      console.error("Error saving defaults:", error);
      throw error;
    }
  };

  const handleItemToggle = (id) => {
    if (expandedItems.includes(id)) {
      setExpandedItems(expandedItems.filter((item) => item !== id));
    } else {
      setExpandedItems([...expandedItems, id]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedOrganizationId]);

  if (loading) {
    return <PricingDefaultsLoader />;
  }

  const accordionItems = pricingModels.map((model) => {
    const isSharedItem = model.key === "common_defaults";

    return {
      id: model.key,
      title: model.title,
      subtitle: isSharedItem
        ? ""
        : activeModels[model.key]
          ? "Active"
          : "Inactive",
      content: (
        <PricingModelAccordionItem
          model={model.key}
          title={model.title}
          isActive={isSharedItem ? true : activeModels[model.key] === 1}
          onToggleActive={isSharedItem ? () => {} : handleToggleActive}
          defaults={defaults[model.key] || {}}
          onSaveDefaults={handleSaveDefaults}
          loading={savingActive}
          readOnly={isSharedItem}
          isExpanded={expandedItems.includes(model.key)}
        />
      ),
    };
  });

  return (
    <div
      className={`${styles.pricingDefaults} ${expandDetails ? styles.expanded : ""}`}
    >
      <Accordion
        items={accordionItems}
        expandedItems={expandedItems}
        onItemToggle={handleItemToggle}
        className={styles.pricingDefaultsAccordion}
      />
    </div>
  );
};

export default PricingDefaults;
